import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { EditorContext } from 'fable/flowEditor/components/flowManager/canvas/types/context';
import {
  ActionNodeT,
  NodeT,
} from 'fable/flowEditor/components/flowManager/types';

import { FlowMessengerNodeSubflowReference } from '../../../flow.types';

type Flow = Pick<EditorContext, 'props'>['props']['flow'];
type Action = NodeT | ActionNodeT;
type SubflowId = string;
type SubflowReferenceNodeId = string;
export type SubflowReferenceCounts = Record<
  SubflowId,
  SubflowReferenceNodeId[]
>;

type SubflowReferenceCheckArg = {
  flow: Flow;
  selectedNodesIds: string[];
};

type SubflowReferenceCheckResult = {
  needsCustomConfirmation: boolean;
  dereferencedSubflowsIds: string[];
};

/**
 * Check if the user is trying to delete a subflow reference node that is the
 * last reference to a subflow. When a subflow reference node is deleted, the
 * subflow will be deleted as well.
 */
export function scanForDereferencedSubflows({
  flow,
  selectedNodesIds,
}: SubflowReferenceCheckArg): SubflowReferenceCheckResult {
  const allActionsToBeDeleted = findHighlightedActionsForDelete(
    selectedNodesIds,
    flow,
  );

  const subflowRefActionsToBeDeleted = findSubflowsHighlightedForDelete(
    allActionsToBeDeleted,
  );

  // Check if the deleted node is a subflow reference and if it's the last reference to the subflow node
  if (subflowRefActionsToBeDeleted.length > 0) {
    /**
     * Subflow will be under delete if there's only **one** reference to it, and
     * that one ref is being deleted.
     */
    const referencesToSubflows = countReferencesToSubflows(flow);

    const dereferencedSubflowIdsSet = findDereferencedSubflows(
      referencesToSubflows,
      subflowRefActionsToBeDeleted,
    );

    if (dereferencedSubflowIdsSet.size > 0) {
      return {
        needsCustomConfirmation: true,
        dereferencedSubflowsIds: [...dereferencedSubflowIdsSet],
      };
    }
  }

  return {
    needsCustomConfirmation: false,
    dereferencedSubflowsIds: [],
  };
}

function findHighlightedActionsForDelete(
  selectedNodeIds: string[],
  flow: Flow,
): Action[] {
  return selectedNodeIds
    .map((nodeId) => flow.actions.find((action) => action.id === nodeId))
    .filter((action): action is NodeT | ActionNodeT => !!action);
}

function findSubflowsHighlightedForDelete(
  highlightedActionsForDelete: Action[],
): FlowMessengerNodeSubflowReference[] {
  return highlightedActionsForDelete.filter(
    (action) => action.nodeType === FlowNodeTypes.SUBFLOW_REFERENCE,
  ) as FlowMessengerNodeSubflowReference[];
}

/** Given a flow, counts the number of references to each subflow */
function countReferencesToSubflows({
  actions,
  subflows,
}: Flow): SubflowReferenceCounts {
  const references: SubflowReferenceCounts = {};

  if (!actions || !subflows || actions.length === 0 || subflows.length === 0) {
    return references;
  }

  actions.forEach((action) => {
    // Only care about subflow references
    if (action.nodeType !== FlowNodeTypes.SUBFLOW_REFERENCE) {
      return;
    }

    // Check if the subflow reference is actually referencing a subflow yet
    if (!action.payload.subflowId) {
      return;
    }

    // Find the subflow that the reference is pointing to
    const subflow = subflows.find(
      (subflow) => subflow.id === action.payload.subflowId,
    );

    const referencedSubflowId = action.payload.subflowId;

    if (!subflow) {
      return;
    }

    // Create a new reference if it doesn't exist
    if (!references[referencedSubflowId]) {
      references[referencedSubflowId] = [];
    }

    // Add the node id to the list of references
    references[referencedSubflowId].push(action.id);
  });

  return references;
}

/**
 * Find subflows that are being deleted because they are **being** dereferenced
 * in the main flow
 */
function findDereferencedSubflows(
  referencesToSubflows: SubflowReferenceCounts,
  actionsToBeDeleted: FlowMessengerNodeSubflowReference[],
): Set<string> {
  const subflowNamesHighlightedForDelete = new Set<string>();

  actionsToBeDeleted.forEach((subflowReferenceAction) => {
    const subflowId = subflowReferenceAction.payload.subflowId;

    // There's only one reference to the subflow, deleting it will make the subflow unaccessible
    if (referencesToSubflows[subflowId].length === 1) {
      subflowNamesHighlightedForDelete.add(subflowId);
    }
  });

  return subflowNamesHighlightedForDelete;
}

export const findSubflowNameById =
  (flow: Flow) =>
  (subflowId: string): string =>
    flow.subflows.find((subflow) => subflow.id === subflowId)?.name ??
    'unknown sub-flow';
